import React, { useState, useMemo, useEffect } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { CreateKioskHardwarePayload } from 'store/kioskHardware';
import { getKioskHardwareInitialForm, kioskHardwareValidationSchema, getHasInvalidInput } from '../../utils';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps } from '../../types';
import { selectors as hardwareSelectors, Hardware } from 'store/hardware';
import { getSortedList } from 'utils/stringFormat';
import { useSelector } from 'react-redux';

export const CreateKioskHardwareForm = (props: ConfigCreateFormProps) => {
  const { kiosk, loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  const hardware = useSelector(hardwareSelectors.selectHardware);

  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(null);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getKioskHardwareInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  useEffect(() => {
    if (kiosk) {
      setForm({ ...form, kioskId: kiosk.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateKioskHardwarePayload = {
      kioskId: form.kioskId,
      hardwareId: form.hardwareId,
      serialCode: form.serialCode ? form.serialCode : '',
      configuration: form.configuration ? JSON.parse(form.configuration as string) : {},
      values: form.values ? JSON.parse(form.values as string) : {},
      status: form.status
    };

    onSubmit(payload);
  };

  const onInputChange = (
    key: keyof ReturnType<typeof getKioskHardwareInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getKioskHardwareInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await kioskHardwareValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getKioskHardwareInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = !selectedHardware || hasInvalidInput;

  // for hardware selection
  const onHardwareChange = (item: Hardware) => {
    setSelectedHardware(item);
    setForm({ ...form, hardwareId: item.id });
  };

  return (
    <FlexBox justify="center" padding="0 0 11" sx={{ flex: '1 1 0%' }}>
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box sx={{ flex: 'none' }} fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Kiosk Hardware</Typography>
          </Box>
          <FlexBox fullWidth direction="row" margin="0 6 0" align="center" minWidth="100%">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Kiosk
            </Typography>
            <Box width={107} />
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              {kiosk && kiosk.name}
            </Typography>
          </FlexBox>
          <FlexBox fullWidth direction="row" margin="6 0" align="center" minWidth="100%" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Hardware
            </Typography>
            <SelectDropdown
              placeholder="Select Hardware"
              items={getSortedList(hardware, 'name')}
              selectedItem={selectedHardware as Hardware}
              onChange={(item: Hardware) => onHardwareChange(item)}
              itemToString={(item: Hardware) => (item ? `${item.name} (${item.type})` : '')}
            />
          </FlexBox>

          <MuiBox className={classes.createClientName}>
            <InputField
              label="serialCode"
              size="medium"
              fullWidth
              placeholder="serialCode"
              value={form.serialCode ? form.serialCode : ''}
              onChange={(e) => onInputChange('serialCode', e)}
              onBlur={(e) => onInputBlur('serialCode', e, e.target.value.trim())}
              helperText={validate('serialCode')}
              error={validation.serialCode === INVALID}
            />
          </MuiBox>

          <MuiBox className={classes.createClientName} sx={{ height: '275px !important' }}>
            <InputField
              multiline
              label="configuration"
              size="medium"
              fullWidth
              placeholder="configuration"
              value={form.configuration ? form.configuration : ''}
              onChange={(e) => onInputChange('configuration', e)}
              rows={8}
              onBlur={(e) => onInputBlur('configuration', e, e.target.value.trim())}
              helperText={validate('configuration')}
              error={validation.configuration === INVALID}
              sx={{ width: '522px', height: '216px', borderRadius: '25px' }}
            />
          </MuiBox>

          <MuiBox className={classes.createClientName} style={{ height: '275px' }}>
            <InputField
              multiline
              label="values"
              size="medium"
              fullWidth
              placeholder="values"
              value={form.values ? form.values : ''}
              onChange={(e) => onInputChange('values', e)}
              rows={8}
              onBlur={(e) => onInputBlur('values', e, e.target.value.trim())}
              helperText={validate('values')}
              error={validation.values === INVALID}
              sx={{ width: '522px', height: '216px', borderRadius: '25px' }}
            />
          </MuiBox>
        </Grid>

        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Kiosk Hardware
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
